import { alpha } from "@mui/material/styles"
import { dsColorNew } from "@palette"
import { HIDDEN_FILTER_CHIP } from "@components/@base/Datatable/FilterHeader"


const overrideTableStyles = theme => ({
	MuiTableCell: {
		styleOverrides: {
			head: {
				backgroundColor: dsColorNew.tableHead,
				color: alpha(dsColorNew.white, 0.92),
				// the data tables' header (User Settings, 7f124f30): one style in every table of the app
				fontWeight: 700,
				fontSize: 14,
				letterSpacing: 'normal',
				textTransform: 'none',
				borderBottom: 'none',
				'&:first-of-type': { borderTopLeftRadius: 8 },
				'&:last-of-type': { borderTopRightRadius: 8 },
			},
			body: {
				color: dsColorNew.textDark,
				borderBottom: `1px solid ${dsColorNew.grayBorder}`,
			}
		}
	},
	MuiButtonBase: {
		styleOverrides: {
			root: {
				'& .MuiMenuItem-root': {
					color: theme.palette.common.white,
				}
			},
		},
	},
	MuiTableSortLabel: {
		styleOverrides: {
			root: {
				'&.Mui-active': {
					'& .MuiTableSortLabel-icon': {
						color: theme.palette.common.white,
					},
				},
			},
		},
	},
	MUIDataTableSelectCell: {
		styleOverrides: {
			headerCell: {
				backgroundColor: dsColorNew.tableHead,
				'& div': {
					'& span': {
						color: alpha(dsColorNew.white, 0.7),
					},
					'& span.Mui-checked': {
						color: dsColorNew.white,
					},
				},
			},
			checkboxRoot: {
				color: dsColorNew.webBlue500
			},
			// the checkbox column stays fixed on horizontal scroll; it takes its row's colour so nothing shows through
			fixedLeft: {
				backgroundColor: 'inherit',
			},
		}
	},
	MUIDataTable: {
		styleOverrides: {
			paper: {
				padding: theme.spacing(2),
				// the funnel stays lit while a filter of the table is on (chips a table drives from elsewhere don't count)
				[`&:has([class*="MUIDataTableFilterList-chip"]:not(.${HIDDEN_FILTER_CHIP})) [data-testid="Filter Table-iconButton"]`]: {
					color: dsColorNew.appBlue,
					backgroundColor: dsColorNew.pillActiveBg,
				},
			},
			responsiveBase: {
				borderTopLeftRadius: 6,
				borderTopRightRadius: 6,
			}
		}
	},
	// a selected row reads the same in every table: soft blue with a blue edge
	MUIDataTableBodyRow: {
		styleOverrides: {
			root: {
				'&.mui-row-selected': {
					backgroundColor: dsColorNew.pillActiveBg,
					boxShadow: `inset 3px 0 0 ${dsColorNew.appBlue}`,
				},
			},
		},
	},
	// Body text at the dashboard table size (13px); cell renders bring their own Typography variants, so those scale here too
	MUIDataTableBodyCell: {
		styleOverrides: {
			root: {
				fontSize: '0.8125rem',
				'& .MuiTypography-body1, & .MuiTypography-body2, & .MuiTypography-subtitle2': { fontSize: '0.8125rem' },
				'& .MuiTypography-subtitle1': { fontSize: '0.875rem' },
				'& .MuiTypography-caption': { fontSize: '0.75rem' },
			}
		}
	},
	MUIDataTableHeadCell: {
		styleOverrides: {
			root: {
				backgroundColor: dsColorNew.tableHead,
				paddingTop: 4,
				paddingBottom: 4,
				// a column without sorting prints its label bare: same case and size as the sortable ones
				'&.MuiTableCell-head': { textTransform: 'none', fontSize: 14, fontWeight: 700, letterSpacing: 'normal' },
			},
			sortActive: {
				color: theme.palette.common.white,
			},
			toolButton: {
				margin: '0px !important',
			}
		}
	},
	MuiToolbar: {
		styleOverrides: {
			root: {
				alignItems: 'flex-start',
				paddingRight: '24px !important',
				paddingLeft: '4px !important',
				marginBottom: 4,
				minHeight: 40,
				'&.app-toolbar': {
					alignItems: "center",
					marginRight: 8,
					gap: 1,
					'& h6': {
						overflow: "visible !important"
					}
				}
			}
		}
	},
	MuiTablePagination: {
		styleOverrides: {
			toolbar: {
				alignItems: "center",
				flexWrap: 'wrap',
				justifyContent: 'flex-end',
			},
			actions: {
				marginRight: 0,
			}
		}
	},
	// chips of the filters in use: the same soft chip as the values picked in the form dropdowns
	MUIDataTableFilterList: {
		styleOverrides: {
			chip: {
				margin: '0 8px 8px 0',
				height: 26,
				borderRadius: 6,
				backgroundColor: dsColorNew.pillActiveBg,
				color: dsColorNew.appBlue,
				fontSize: 12,
				fontWeight: 500,
				'& .MuiChip-label': { paddingLeft: 10, paddingRight: 6 },
				'& .MuiChip-deleteIcon': { color: dsColorNew.appBlue, opacity: 0.7, fontSize: 16, marginRight: 6, '&:hover': { color: dsColorNew.appBlue, opacity: 1 } },
			},
			// setFilterChipProps' className replaces the chip class, so the hidden chip is caught from the list
			root: {
				[`& .${HIDDEN_FILTER_CHIP}`]: { display: 'none' },
			},
		}
	},
	// The filter panel (funnel): AppTableFilterHeader is rendered as its footer and moved to the top here;
	// the framework's own title is hidden and its Reset sits beside the ×
	MUIDataTableFilter: {
		styleOverrides: {
			root: {
				display: 'flex',
				flexDirection: 'column',
				padding: 0,
				backgroundColor: dsColorNew.white,
				fontFamily: 'inherit',
				'& > .MuiGrid-container': { width: 'auto', margin: 0, padding: '12px 10px 16px' },
				'& > .MuiGrid-container > .MuiGrid-item': { padding: '7px 8px' },
				'& .MuiFormControl-root label': {
					position: 'relative', transform: 'none',
					marginBottom: 6, fontSize: 12, fontWeight: 500, color: dsColorNew.textMuted,
					'&.Mui-focused': { color: dsColorNew.textMuted },
				},
				'& .MuiFormControl-root label + .MuiInput-root': { marginTop: 0 },
				'& .MuiInput-root': {
					minHeight: 36,
					paddingLeft: 12,
					borderRadius: 8,
					border: `1px solid ${dsColorNew.grayBorder}`,
					backgroundColor: dsColorNew.white,
					fontSize: 13,
					color: dsColorNew.textTitle,
					'&::before, &::after': { display: 'none' },
					'&.Mui-focused': { borderColor: dsColorNew.appBlue },
					'& .MuiSelect-select': { paddingTop: 7, paddingBottom: 7, backgroundColor: 'transparent' },
					'& .MuiSelect-icon': { color: dsColorNew.textSubtle },
				},
				// a field in use lights up
				'& .MuiFormControl-root:has(.MuiSelect-nativeInput:not([value="All"]):not([value=""])) .MuiInput-root': {
					borderColor: dsColorNew.appBlue,
					backgroundColor: dsColorNew.pillActiveBg,
					color: dsColorNew.appBlue,
					fontWeight: 500,
					'& .MuiSelect-icon': { color: dsColorNew.appBlue },
				},
				'& .MuiCheckbox-root.Mui-checked': { color: dsColorNew.appBlue },
			},
			header: { margin: 0, padding: 0 },
			title: { display: 'none' },
			resetLink: {
				position: 'absolute',
				top: 18,
				right: 56,
				zIndex: 101,
				height: 30,
				minWidth: 0,
				margin: 0,
				padding: '0 8px',
				borderRadius: 6,
				backgroundColor: 'transparent',
				boxShadow: 'none',
				color: dsColorNew.appBlue,
				textTransform: 'none',
				fontSize: 13,
				fontWeight: 500,
				'&:hover': { backgroundColor: dsColorNew.hoverBgLight, boxShadow: 'none' },
			},
			gridListTile: { marginTop: 0 },
			checkboxListTitle: { marginBottom: 2, fontSize: 12, fontWeight: 500, color: dsColorNew.textMuted },
			checkboxFormControlLabel: { '& .MuiTypography-root': { fontSize: 13, color: dsColorNew.textTitle } },
		}
	},
	MUIDataTableToolbar: {
		styleOverrides: {
			icon: { borderRadius: 8 },
			iconActive: { borderRadius: 8, color: dsColorNew.appBlue, backgroundColor: dsColorNew.pillActiveBg },
			filterPaper: {
				borderRadius: 14,
				border: `1px solid ${dsColorNew.grayBorder}`,
				boxShadow: `0 16px 40px ${alpha(dsColorNew.navy, 0.16)}`,
			},
			filterCloseIcon: {
				top: 18,
				right: 14,
				width: 30,
				height: 30,
				borderRadius: 8,
				color: dsColorNew.textSubtle,
				'& svg': { fontSize: 20 },
			},
			actions: {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'flex-end',
				gap: 4,
				// the buttons take what they need; when the line is full they wrap onto a second
				// one, right-aligned, instead of running past the card
				flex: '0 1 auto',
				flexWrap: 'wrap',
				minWidth: 0,
				rowGap: 8,
				whiteSpace: 'nowrap',
				'& .MUIDataTableToolbar-customActions': {
					border: 0,
					paddingTop: 0
				},
			}
		}
	},
	MUIDataTableToolbarSelect: {
		styleOverrides: {
			root: {
				marginBottom: 16,
				// a floating selection bar (AppSelectionBar) leaves no box of its own above the table
				'&:has(.app-selection-bar)': { display: 'contents' },
				'&:has(.app-selection-bar) > :not(.app-selection-bar)': { display: 'none' },
			}
		}
	}
})

export default overrideTableStyles